import { Divider, Skeleton, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TranslateText from "../../resuableComponents/TranslateText";
import "./paymentOptionsContainer.css";
import "../fontStyling.css";
import { useDispatch, useSelector } from "react-redux";
import {
  colorShadesGenerator,
  formatPaymentDictionary,
  generateDynamicStyling,
  getStandaloneAffirmItem,
  getIsWalletItemAvailable,
  isEmpty,
} from "../../../Utils/utils";
import { changeActivePaymentMethod, requestPaymentMethod } from "../../../slices/PaymentButtonSlice";
import { handleTotalPaymentSummaryError } from "../../../slices/HandleAdditionalComponentsSlice";
import { getAllPaymentMethods } from "../../../slices/PaymentSessionDetailsSlice";
import { setOfferMisMatchModal, setSelectedActivePaymentTab } from "../../../slices/OfferSlice";
import WalletPay from "./WalletPay/WalletPay";
import { removeSelectedIntruments } from "../../../slices/SavedInstrumentSlice";
import { showRetryLimitReached } from "../../../slices/PaymentInitiationSlice";
import { TOP_BANKS, UPI_INTENT_APPS } from "../../../staticData";

const PaymentOptionsContainer = ({
  tabList,
  darkColor,
  lightColor,
  handleSelectedTabId,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState();
  // Distinguishes a real tab click from the tab being auto-selected on load /
  // data refresh, so PAYMENT_CATEGORY_SELECTED only reports actual clicks.
  const hasUserSelectedTabRef = useRef(false);
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const paymentMethodDictionary = useSelector((state) => state.paymentSessionDetails.paymentMethodDictionary);
  const paymentMethodDictionaryAll = useSelector((state) => state.paymentSessionDetails.paymentMethodDictionaryAll);
  const sessionDetailsLoading = useSelector((state) => state.paymentSessionDetails.sessionDetailsLoading);
  const selectedActivePaymentTab = useSelector(state => state?.offers?.selectedActivePaymentTab)
  const appliedOfferDetail = useSelector(state => state.offers.appliedOfferDetail)
  const showOfferMisMatchModal = useSelector(state => state.offers.showOfferMisMatchModal)
  const methodAttemptsLeft = useSelector(state => state.paymentInitiation.methodAttemptsLeft)
  const requestedPaymentMethod = useSelector(state => state.paymentButton.requestedPaymentMethod)
  const isGooglePayAvailableRender = useSelector(state => state.gpayDetails.isGooglePayAvailable)
  const isApplePayAvailableRender = useSelector(state => state.gpayDetails.isApplePayAvailable)

  const isGooglePayAvailable = getIsWalletItemAvailable(paymentMethodDictionaryAll, "GooglePay")
  const isApplePayAvailable = getIsWalletItemAvailable(paymentMethodDictionaryAll, "ApplePay")
  const isRevolutPayAvailable = getIsWalletItemAvailable(paymentMethodDictionaryAll, "RevolutPay")
  // Express wallet-strip button (where Google Pay & Apple Pay live) — driven by
  // whether the session actually advertises Affirm now, same as the wallet brands above.
  const SHOW_AFFIRM_EXPRESS = !!getStandaloneAffirmItem(paymentMethodDictionaryAll)
  // Express-only brands (GooglePay/ApplePay/RevolutPay/Affirm) are stripped out of
  // the generic tab dictionary by formatPaymentDictionary, so a session offering
  // ONLY an express brand leaves that dictionary empty. Used below so the whole
  // Payment Options section (and the wallet strip within it) doesn't collapse to
  // "No payment option available" in that case.
  const hasExpressWalletAvailable = (!isEmpty(isGooglePayAvailable?.additionalData) && isGooglePayAvailableRender)
    || (!isEmpty(isApplePayAvailable?.additionalData) && isApplePayAvailableRender)
    || isRevolutPayAvailable
    || SHOW_AFFIRM_EXPRESS
  // Whether there's anything left for the generic tab list below the express
  // strip - false when express brands (Affirm/GooglePay/etc.) are the only
  // methods the session offers, since formatPaymentDictionary strips them out
  // of paymentMethodDictionary. Gates the "or pay with" divider and the tab
  // list container so an express-only session doesn't show either.
  const hasGenericPaymentMethods = Object.keys(paymentMethodDictionary || {}).length > 0


  const paymentMethodDictionaryFormatted = formatPaymentDictionary(paymentMethodDictionary)

  const getTabPreviewIcons = (type) => {
    if (type === "Upi" || type === "UpiOneTimeMandate") {
      return UPI_INTENT_APPS;
    }
    const entries = paymentMethodDictionary[type];
    if (!entries?.length) return [];
    switch (type) {
      case "NetBanking": {
        const priorityMatches = TOP_BANKS
          .map((bankName) => entries.find((entry) => entry?.title === bankName))
          .filter(Boolean);
        const remaining = entries.filter((entry) => !priorityMatches.includes(entry));
        return [...priorityMatches, ...remaining].slice(0, 3);
      }
      case "Card":
        return entries.slice(0, 3);
      case "Wallet":
      case "BuyNowPayLater":
        return entries.length > 1 ? entries.slice(0, 3) : [];
      case "Emi": {
        const getIssuerTitle = (entry) =>
          entry?.emiMethod?.issuerTitle || entry?.emiMethod?.cardlessEmiProviderTitle;
        const priorityMatches = TOP_BANKS
          .map((bankName) => entries.find((entry) => getIssuerTitle(entry) === bankName))
          .filter(Boolean);
        const remaining = entries.filter((entry) => !priorityMatches.includes(entry));
        return [...priorityMatches, ...remaining].slice(0, 3);
      }
      default:
        return [];
    }
  };

  const handleChangeSelectedTab = (id) => {
    const selectedTabItem = tabList[id];
    // Attempts for this payment method are used up: show the error and keep the current tab.
    if (methodAttemptsLeft[selectedTabItem?.type] === 0) {
      dispatch(showRetryLimitReached({ reasonCode: "BE_1815" }));
      return;
    }
    // const isEmiType = selectedTabItem?.type?.toLowerCase() === 'emi';
    // const hasAppliedOffer = appliedOfferDetail?.codes?.length;
    // // console.log("isEmiType", isEmiType, "hasAppliedOffer", hasAppliedOffer)
    // if (isEmiType && hasAppliedOffer) {
    //   dispatch(setOfferMisMatchModal(true));
    // }
    if (id !== selectedTab) {
      dispatch(removeSelectedIntruments());
    }
    hasUserSelectedTabRef.current = true;
    dispatch(setSelectedActivePaymentTab(id));
    setSelectedTab(id);
    dispatch(handleTotalPaymentSummaryError(""));
  };
  // Another part of the page (retry limit modal) asked for a method: open its tab.
  useEffect(() => {
    if (!requestedPaymentMethod) return;
    const requestedTabIndex = tabList.findIndex((tab) => tab?.type === requestedPaymentMethod);
    if (requestedTabIndex >= 0) {
      handleChangeSelectedTab(requestedTabIndex);
    }
    dispatch(requestPaymentMethod(null));
  }, [requestedPaymentMethod]);

  useEffect(() => {
    if (!paymentMethodDictionary) return;

    // A tab is selectable only when its method exists in the dictionary. A UPI tab
    // that only carries an intent app is hidden in the list below, so it must not
    // be treated as available/selectable here either.
    const isTabAvailable = (type) =>
      !!type &&
      type in paymentMethodDictionary &&
      (type !== "Upi" || shouldDisplayUpi(paymentMethodDictionary[type]));

    const isOnlyUpiIntent =
      paymentMethodDictionary["Upi"]?.length === 1 &&
      paymentMethodDictionary["Upi"][0]?.brand === "UpiIntent";

    const firstAvailableIdx = tabList.findIndex((tab) =>
      isOnlyUpiIntent
        ? tab?.type !== "Upi" && isTabAvailable(tab?.type)
        : isTabAvailable(tab?.type)
    );

    // Resolve the target tab deterministically so the auto-selection cannot get
    // stuck in an in-between state across the multiple dictionary updates that
    // happen while methods load (this is what made a single available method
    // sometimes end up unselected). Priority:
    //   1. the method the shopper last paid with (persisted in localStorage)
    //   2. the tab already active in redux (kept across refetches)
    //   3. the first available tab
    const lastUsedType = localStorage.getItem("previousActivePaymentTab");
    const lastUsedIdx = tabList.findIndex(
      (tab) => tab?.type === lastUsedType && isTabAvailable(tab?.type)
    );
    const reduxType = tabList[selectedActivePaymentTab]?.type;

    let targetIdx;
    if (lastUsedIdx >= 0) targetIdx = lastUsedIdx;
    else if (isTabAvailable(reduxType)) targetIdx = selectedActivePaymentTab;
    else targetIdx = firstAvailableIdx;

    if (targetIdx < 0) return;

    if (
      selectedTab !== undefined &&
      selectedTab !== null &&
      targetIdx !== selectedTab
    ) {
      // switching tabs clears any instrument picked on the previous tab
      dispatch(removeSelectedIntruments());
      dispatch(handleTotalPaymentSummaryError(""));
    }

    setSelectedTab(targetIdx);
    if (targetIdx !== selectedActivePaymentTab) {
      dispatch(setSelectedActivePaymentTab(targetIdx));
    }
  }, [paymentMethodDictionary]);

  useEffect(() => {
    if (sessionDetails) {
      generateDynamicStyling(sessionDetails);
      const shades = colorShadesGenerator(
        sessionDetails?.merchantDetails?.checkoutTheme?.headerColor
      );
    }
    dispatch(changeActivePaymentMethod(tabList[selectedActivePaymentTab]?.type || tabList[selectedTab]?.type));
  }, [sessionDetails, selectedTab]);
  useEffect(() => {
    if (Object.keys(paymentMethodDictionary)?.length >= 0) {
      const timer = setTimeout(() => {
        setIsPaymentLoading(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [paymentMethodDictionary]);

  useEffect(() => {
    // Skip auto-selection (page load / data refresh) — only report real clicks.
    // selectedTab is a tab index, so 0 (the first tab) must stay a valid value.
    if (selectedTab !== undefined && selectedTab !== null && hasUserSelectedTabRef.current) {
      handleSelectedTabId(selectedTab);
    }
  }, [selectedTab]);


  useEffect(() => {
    if (
      selectedActivePaymentTab !== null &&
      selectedActivePaymentTab !== undefined &&
      tabList[selectedActivePaymentTab]?.type in paymentMethodDictionary
    ) {
      setSelectedTab(selectedActivePaymentTab);
    }
  }, [selectedActivePaymentTab])

  // Ensure selectedTab always points to a tab whose type actually exists in the
  // paymentMethodDictionary. Runs only when selectedTab is empty or its current
  // type is not a key in the dictionary.
  useEffect(() => {
    if (!tabList?.length || isEmpty(paymentMethodDictionary)) return;

    const isCurrentTabValid =
      !isEmpty(selectedTab) &&
      tabList[selectedTab]?.type in paymentMethodDictionary;

    if (isCurrentTabValid) return;

    // Prefer the active payment tab if its type is present in the dictionary.
    const activeType = tabList[selectedActivePaymentTab]?.type;
    if (activeType && activeType in paymentMethodDictionary) {
      setSelectedTab(selectedActivePaymentTab);
      return;
    }

    // Fall back to the tab that matches the first key of the dictionary.
    const [topKey] = Object.keys(paymentMethodDictionary);
    const fallbackIndex = tabList.find((tab) => tab?.type === topKey);
    if (fallbackIndex?.id) {
      setSelectedTab(fallbackIndex.id);
    }
  }, [selectedTab, selectedActivePaymentTab, paymentMethodDictionary, tabList]);

  const shouldDisplayUpi = (upiMethods) => {
    return upiMethods?.some((method) => method?.brand !== "UpiIntent");
  };
  return (
    <>
      {isPaymentLoading || paymentMethodDictionary === undefined || sessionDetailsLoading ? (
        <>
          <Skeleton active size="large" />
        </>
      ) : Object?.keys(paymentMethodDictionary)?.length || hasExpressWalletAvailable ? (
        <div className={`domestic-po-root-container${!hasGenericPaymentMethods ? ' domestic-po-root-container--express-only' : ''}`}>

          {hasExpressWalletAvailable &&
            <>
              <div className="domestic-payment-header">
                <img
                  src="/assets/cardHeader/PaymentOption.svg"
                  alt="card"
                  style={{ height: "20px", widht: "20px" }}
                />
                <Typography
                  className="semiBold16Heading dynamic-font-family"
                  style={{ color: "var(--Neutral-Grey-1200)" }}
                >
                  <TranslateText label={"PAYMENT_OPTIONS"}>
                    Payment Options
                  </TranslateText>
                </Typography>
              </div>
              <div className="payment-wallet-container">
                <WalletPay />
              </div>
            </>
          }
          {hasExpressWalletAvailable && hasGenericPaymentMethods &&
            <Divider plain className="or-pay-with-divider">
              < TranslateText label={"OR_PAY_WITH"} > or pay with</TranslateText>
            </Divider >
          }
          {/* Must mirror hasExpressWalletAvailable: the express block above already renders its own
              title, so this standalone title shows only when that block is absent. Checking mere
              presence of the GooglePay/ApplePay item (without additionalData) hid both titles. */}
          {!hasExpressWalletAvailable && (
            <div className="domestic-po-header">
              <img
                src="/assets/cardHeader/PaymentOption.svg"
                alt="card"
                style={{ height: "20px", widht: "20px" }}
              />
              <Typography
                className="semiBold16Heading dynamic-font-family"
                style={{ color: "var(--Neutral-Grey-1200)" }}
              >
                <TranslateText label={"PAYMENT_OPTIONS"}>
                  Payment Options
                </TranslateText>
              </Typography>
            </div>
          )}
          {hasGenericPaymentMethods && (
            <div className="domestic-po-root">
              {/* po-tabs-v2 scopes the redesigned tab list styling in paymentOptionsContainer.css
                (marked "REDESIGN V2"). To revert to the original look, remove this class
                (and the po-icon-badge/po-chevron wrappers below) - no other logic changes. */}
              <div className="domestic-po-header-tabs po-tabs-v2">
                {tabList?.map(
                  (tab, id) =>
                    (tab?.type !== "Upi" ||
                      shouldDisplayUpi(paymentMethodDictionary[tab?.type])) &&
                    tab?.type in paymentMethodDictionary && (
                      <div
                        key={id}
                        className={`domestic-po-tab po-tab-v2${selectedTab === id ? " po-tab-v2-active" : ""}`}
                        style={{
                          backgroundColor: selectedTab === id ? lightColor : "",
                          borderLeft:
                            selectedTab === id
                              ? `5px solid ${darkColor}`
                              : `5px solid #fff`,
                        }}
                        onClick={() => { handleChangeSelectedTab(id) }}
                      >
                        {paymentMethodDictionary[tab?.type]?.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="po-icon-badge"
                              style={{
                                backgroundColor: selectedTab === id ? darkColor : "",
                              }}
                            >
                              {selectedTab === id ? tab?.activeLogo : tab?.logo}
                            </span>
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "6px",
                              }}
                            >
                              <Typography
                                className={
                                  selectedTab === id
                                    ? "tab-title-heading-active dynamic-font-family"
                                    : "tab-title-heading dynamic-font-family"
                                }
                              >
                                {(() => {
                                  const isSingleBrandedInstrument = ["Wallet", "BuyNowPayLater"].includes(
                                    paymentMethodDictionary[tab?.type][0]?.type
                                  ) && paymentMethodDictionary[tab?.type].length === 1;
                                  if (isSingleBrandedInstrument) {
                                    return paymentMethodDictionary[tab?.type][0]?.title;
                                  }
                                  if (tab?.type === "BuyNowPayLater") {
                                    return (
                                      <>
                                        Buy Now
                                        <br />
                                        Pay Later
                                      </>
                                    );
                                  }
                                  return (
                                    <TranslateText label={tab.type.toUpperCase()}>
                                      {tab?.type === "Recommended"
                                        ? "Recommended"
                                        : paymentMethodDictionary[tab?.type][0]?.typeTitle}
                                    </TranslateText>
                                  );
                                })()}
                              </Typography>
                              {getTabPreviewIcons(tab?.type).length > 0 && (
                                <div className="po-preview-icon-group-desktop" style={{ marginLeft: "auto" }}>
                                  {getTabPreviewIcons(tab?.type).map((entry, iconIdx) => (
                                    <img
                                      key={entry?.id || iconIdx}
                                      src={entry?.logoUrl || entry?.logo}
                                      alt={entry?.title || entry?.name}
                                      className={`po-preview-icon-desktop${(tab?.type === "Upi" || tab?.type === "UpiOneTimeMandate") ? " po-preview-icon-small-desktop" : ""}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                )}
              </div>
              <div className="domestic-po-content">
                {tabList?.length > 0 &&
                  tabList[selectedTab]?.content &&
                  tabList[selectedTab]?.type in paymentMethodDictionary ? (
                  tabList[selectedTab]?.content
                ) : (
                  <></>
                  // <Skeleton active size="large" />
                )}
              </div>
            </div>
          )}
        </div >
      ) : (
        <div className="domestic-empty-po-root">
          <div className="no-payment-options-div">
            <img
              src="/assets/paymentOptions/paymentoptions.svg"
              alt="payment"
            />
            <Typography className="no-payment-options-Heading dynamic-font-family">
              <TranslateText label={"NO_PAYMENT_OPTION_AVAILABLE"}>
                No payment option available
              </TranslateText>
            </Typography>
            <Typography className="no-payment-options-subHeading dynamic-font-family">
              <TranslateText label={"CHANGE_COUNTRY_FOR_PAYMENT_OPTIONS"}>
                Please change your country in the address to see the available
                payment options
              </TranslateText>
            </Typography>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentOptionsContainer;
